import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, output } from '@angular/core'
import { CommonModule } from '@angular/common'
import { RouterLink } from '@angular/router'
import { ButtonModule } from '@openng/optimus-ui/button'
import { Tag } from '@openng/optimus-ui/tag'

import { EquipmentDoc } from '../../../core/equipment-search.service'
import { conditionLabel, equipmentDocCoverUrl, formatToman } from './equipment-labels'

/**
 * Equipment ad card (M3) — used by the search grid and the similar-items rail
 * on the detail page. Cover = elastic `fileId[0]` (falling back to the legacy
 * media fields) with a local inline-SVG placeholder on missing/failed loads.
 * Badge cap: max 3 (پرداخت امن / قابل مذاکره / عمده).
 *
 * Every part of the card reserves its own height (badges, 2-line title, brand,
 * city, footer), so cards keep identical dimensions whatever the ad contains.
 */
@Component({
	selector: 'app-equipment-card',
	standalone: true,
	imports: [CommonModule, RouterLink, ButtonModule, Tag],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<article
			class="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group flex flex-col h-full min-w-0">
			<div class="relative aspect-[4/3] shrink-0 bg-slate-100 overflow-hidden">
				@if (cover(); as cover) {
					<img
						[src]="cover"
						[alt]="doc().title ?? ''"
						(error)="imageFailed.set(true)"
						loading="lazy"
						class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
				} @else {
					<div class="w-full h-full bg-gradient-to-br from-purple-100 to-pink-50 flex items-center justify-center">
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="1.5"
							class="w-12 h-12 text-purple-300"
							aria-hidden="true">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085" />
						</svg>
					</div>
				}
				@if (doc().equipmentSecurePayment) {
					<span
						class="absolute top-3 right-3 bg-emerald-500 text-white text-[10px] font-black px-2.5 py-1 rounded-lg shadow inline-flex items-center gap-1">
						<i class="pi pi-shield"></i> پرداخت امن
					</span>
				}
				@if (doc().equipmentIsExchangeable) {
					<span
						class="absolute top-3 left-3 bg-purple-600/95 text-white text-[10px] font-black px-2.5 py-1 rounded-lg shadow inline-flex items-center gap-1">
						<i class="pi pi-arrows-h"></i> مایل به معاوضه
					</span>
				}
				@if (showFavorite()) {
					<button
						[attr.aria-label]="favorite() ? 'حذف از علاقه‌مندی‌ها' : 'افزودن به علاقه‌مندی‌ها'"
						(click)="favoriteToggle.emit()"
						type="button"
						class="absolute bottom-3 left-3 w-9 h-9 rounded-xl bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors">
						<i class="pi {{ favorite() ? 'pi-heart-fill text-red-500' : 'pi-heart text-slate-400' }}"></i>
					</button>
				}
			</div>
			<div class="p-4 flex flex-col flex-1 min-w-0">
				<!-- Badge row: fixed height so a card with fewer tags still lines up. -->
				<div class="flex items-center flex-nowrap gap-1.5 h-7 mb-2 overflow-hidden">
					@if (doc().equipmentCondition) {
						<p-tag [value]="conditionLabel(doc().equipmentCondition)" [rounded]="true" severity="secondary" />
					}
					@if (doc().equipmentNegotiable) {
						<p-tag [rounded]="true" value="قابل مذاکره" severity="warn" />
					}
					@if (doc().equipmentWholesale) {
						<p-tag [rounded]="true" value="عمده" severity="info" />
					}
				</div>
				<!-- Title: always two lines tall, longer titles clamp. -->
				<a
					[routerLink]="['/equipment', doc().id + '-' + (doc().slug || '')]"
					[attr.title]="doc().title || null"
					class="font-black text-slate-800 text-sm leading-6 line-clamp-2 h-12 hover:text-purple-600 transition-colors">
					{{ doc().title || 'بدون عنوان' }}
				</a>
				<p [attr.title]="brandModel() || null" class="text-xs leading-4 h-4 mt-1 text-slate-400 truncate">
					{{ brandModel() || '—' }}
				</p>
				<p class="text-[11px] leading-4 h-4 mt-1 truncate">
					@if (doc().cityName) {
						<span class="text-slate-500"><i class="pi pi-map-marker text-purple-400"></i> {{ doc().cityName }}</span>
					} @else {
						<span class="text-slate-300">—</span>
					}
				</p>
				<div class="mt-auto flex items-center justify-between gap-2 pt-3 border-t border-gray-50">
					<span class="font-black text-slate-800 text-sm truncate min-w-0">
						@if (doc().contactForPrice) {
							<span class="text-xs">تماس برای قیمت</span>
						} @else if (doc().minEffectivePrice !== null && doc().minEffectivePrice !== undefined) {
							@if ((doc().minEffectivePrice ?? 0) > 0) {
								<span class="text-[10px] font-bold text-slate-400">از </span>
							}
							{{ formatToman(doc().minEffectivePrice) }}
						} @else {
							<span class="text-xs">تماس برای قیمت</span>
						}
					</span>
					<button
						[text]="true"
						[label]="inCompare() ? 'در مقایسه ✓' : 'مقایسه'"
						(click)="compareToggle.emit()"
						pButton
						size="small"
						class="shrink-0"></button>
				</div>
			</div>
		</article>
	`,
})
export default class EquipmentCard {
	readonly doc = input.required<EquipmentDoc>()
	readonly favorite = input(false)
	readonly showFavorite = input(true)
	readonly inCompare = input(false)

	readonly favoriteToggle = output<void>()
	readonly compareToggle = output<void>()

	/** Resets whenever the doc's cover changes, so a new ad can retry loading. */
	readonly imageFailed = linkedSignal(() => !equipmentDocCoverUrl(this.doc()))

	readonly cover = computed(() => (this.imageFailed() ? null : equipmentDocCoverUrl(this.doc())))
	readonly brandModel = computed(() =>
		[this.doc().equipmentBrandName, this.doc().equipmentModelName].filter(Boolean).join(' / ')
	)

	readonly formatToman = formatToman
	readonly conditionLabel = conditionLabel
}
